// SWAP POINT: recipe extraction. One entry point for every way a user can hand
// us a recipe, replacing Stage 1's parseInput.
//
// WHAT IS REAL AND WHAT IS NOT — read this before trusting anything below.
//
//   'text'        REAL. Delegates to lib/parseRecipeText, which is honest
//                 deterministic parsing of text the user already gave us.
//                 Nothing is faked on this path.
//   'link'        MOCK. Returns canned catalog recipes for a handful of
//                 example hosts. A real build replaces the body with a fetch;
//                 keep the signature, the returned shape and the error reasons.
//   'video-file'  NOT IMPLEMENTED, and says so. There is no video
//                 understanding in this app. It throws 'unsupported-media'
//                 rather than returning a plausible recipe, because a recipe
//                 the user believes came from their video — and didn't — is
//                 worse than no recipe at all. The picked video is still
//                 playable; playback is not extraction, and the UI must never
//                 let one imply the other.
//
// The iOS release needs either working extraction on these paths or this same
// honest message. It must not ship the demo behaviour as if it were real.

import { delay } from './delay.js';
import { RECIPE_CATALOG } from './recipeCatalog.js';
import { isUrl } from '../lib/matchRecipes.js';
import { parseRecipeText } from '../lib/parseRecipeText.js';

// Only a genuinely temporary failure is worth a retry button. Offering "try
// again" on a private video trains people to mash a button that cannot work.
const TEMPORARY = new Set(['network', 'timeout']);

export class ExtractionError extends Error {
  constructor(reason, message) {
    super(message);
    this.name = 'ExtractionError';
    this.reason = reason;
    this.retryable = TEMPORARY.has(reason);
  }
}

export function isCancellation(error) {
  return error?.name === 'ExtractionError' && error.reason === 'cancelled';
}

// SWAP POINT DATA: which hosts a real extractor would support, and the
// example hosts that exercise each failure. Replace wholesale.
const SUPPORTED_HOSTS = new Set([
  'recipes.example.com',
  'videos.example.com',
  'cliptable.example',
  'kitchen.example.org',
]);
const PRIVATE_HOSTS = new Set(['private.example.com']);
const FLAKY_HOSTS = new Set(['flaky.example.com']);

function hostOf(url) {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

// Cancellation has to be observable while the request is in flight, not only
// after it resolves — otherwise "cancel" just hides a result that still lands.
function abortable(signal) {
  if (!signal) return null;
  if (signal.aborted) return Promise.reject(new ExtractionError('cancelled', 'Cancelled.'));
  return new Promise((_, reject) => {
    signal.addEventListener(
      'abort',
      () => reject(new ExtractionError('cancelled', 'Cancelled.')),
      { once: true }
    );
  });
}

async function wait(ms, signal) {
  const cancel = abortable(signal);
  return cancel ? Promise.race([delay(ms), cancel]) : delay(ms);
}

// Pick something plausible out of the catalog for a supported link. Mock only:
// a real extractor reads the page instead of guessing from the slug.
function catalogFor(url) {
  const slug = url.toLowerCase();
  return (
    RECIPE_CATALOG.find((recipe) => slug.includes(recipe.id)) ??
    RECIPE_CATALOG.find((recipe) => slug.includes(recipe.dishName.split(' ').pop().toLowerCase())) ??
    RECIPE_CATALOG[0]
  );
}

// Creator and site live on `origin` alongside the URL. There is deliberately
// no second attribution object: one recipe, one statement of where it came
// from, so the two can never drift apart or disagree about the link.
function originFor(url, host) {
  const creators = {
    'videos.example.com': 'Mika Tan',
    'cliptable.example': 'The Cliptable Kitchen',
    'recipes.example.com': 'Weeknight Kitchen',
    'kitchen.example.org': null,
  };
  const creator = creators[host] ?? null;

  return {
    kind: host.startsWith('videos.') || host.startsWith('cliptable.') ? 'video' : 'web',
    label: creator ?? host,
    url,
    creator,
    siteName: host,
  };
}

async function extractFromLink(url, signal) {
  const host = hostOf(url);
  if (!host) throw new ExtractionError('unreadable', "That doesn't look like a link we can open.");

  await wait(900, signal);

  if (PRIVATE_HOSTS.has(host)) {
    throw new ExtractionError('private', 'That link is private, so we can’t open it.');
  }
  if (FLAKY_HOSTS.has(host)) {
    throw new ExtractionError('network', 'We couldn’t reach that link just now.');
  }
  if (!SUPPORTED_HOSTS.has(host)) {
    throw new ExtractionError('unsupported', `We don’t read recipes from ${host} yet.`);
  }

  const recipe = catalogFor(url);
  const origin = originFor(url, host);

  return {
    ...recipe,
    id: recipe.id,
    origin,
    // A video host gives us something playable; a recipe page usually doesn't.
    // This is a durable remote URL, which is the only kind that belongs here.
    media: origin.kind === 'video' ? { videoUrl: url } : recipe.media,
  };
}

// The single public entry point. All three routes converge on the canonical
// recipe shape; `servings` and `totalMinutes` may be null when the source
// never stated them, and callers must render that gap rather than fill it.
export async function extractRecipe({ source, value, fileName, signal } = {}) {
  const raw = String(value || '').trim();

  if (source === 'video-file') {
    await wait(400, signal);
    throw new ExtractionError(
      'unsupported-media',
      `We can’t read a recipe out of ${fileName || 'a video file'} yet.`
    );
  }

  if (!raw) throw new ExtractionError('unreadable', 'There was nothing to read.');

  if (source === 'link') {
    if (!isUrl(raw)) throw new ExtractionError('unreadable', "That doesn't look like a link.");
    return extractFromLink(raw, signal);
  }

  if (source === 'text') {
    // Short enough to feel instant, but still a real await so the loading
    // state exists on this path too.
    await wait(250, signal);
    const parsed = parseRecipeText(raw);
    if (parsed.ingredients.length === 0) {
      throw new ExtractionError('unreadable', "We couldn't find any ingredients in that.");
    }
    return parsed;
  }

  throw new ExtractionError('unreadable', `Unknown source: ${source}`);
}
